import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const MarketingKitModal = ({ isOpen, onClose }) => {
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const [includeCustomization, setIncludeCustomization] = useState(false);

  const marketingKitCategories = [
    {
      category: 'Essential Materials',
      materials: [
        {
          id: 'flyer_basic',
          name: 'Basic Service Flyer',
          description: 'General overview of transfer services',
          size: '2.4 MB',
          formats: ['PDF', 'PNG']
        },
        {
          id: 'rate_card',
          name: 'Rate Card & Fee Schedule',
          description: 'Current rates and fee structure',
          size: '1.8 MB',
          formats: ['PDF']
        },
        {
          id: 'business_cards',
          name: 'Agent Business Card Template',
          description: 'Customizable business cards',
          size: '15.2 MB',
          formats: ['PSD', 'PDF']
        }
      ]
    },
    {
      category: 'Promotional Materials',
      materials: [
        {
          id: 'promo_holiday',
          name: 'Holiday Promotion Pack',
          description: 'Seasonal promotional materials',
          size: '8.5 MB',
          formats: ['PDF', 'PNG']
        },
        {
          id: 'promo_referral',
          name: 'Referral Program Materials',
          description: 'Customer referral incentive materials',
          size: '3.2 MB',
          formats: ['PDF']
        },
        {
          id: 'testimonials',
          name: 'Customer Testimonials Booklet',
          description: 'Real customer success stories',
          size: '4.1 MB',
          formats: ['PDF']
        }
      ]
    },
    {
      category: 'Digital Assets',
      materials: [
        {
          id: 'social_templates',
          name: 'Social Media Templates',
          description: 'Ready-to-use social posts',
          size: '45.7 MB',
          formats: ['ZIP']
        },
        {
          id: 'email_templates',
          name: 'Email Marketing Templates',
          description: 'Professional email designs',
          size: '8.3 MB',
          formats: ['HTML', 'ZIP']
        },
        {
          id: 'web_banners',
          name: 'Website Banner Set',
          description: 'Various web banner sizes',
          size: '12.4 MB',
          formats: ['ZIP']
        }
      ]
    },
    {
      category: 'Training Resources',
      materials: [
        {
          id: 'training_manual',
          name: 'Agent Training Manual',
          description: 'Complete agent guidelines',
          size: '12.8 MB',
          formats: ['PDF']
        },
        {
          id: 'compliance_guide',
          name: 'Compliance Guidelines',
          description: 'Regulatory compliance information',
          size: '6.8 MB',
          formats: ['PDF']
        },
        {
          id: 'faq_sheet',
          name: 'Customer FAQ Sheet',
          description: 'Common questions and answers',
          size: '1.1 MB',
          formats: ['PDF']
        }
      ]
    }
  ];

  const handleToggleMaterial = (materialId) => {
    setSelectedMaterials(prev => {
      if (prev?.includes(materialId)) {
        return prev?.filter(id => id !== materialId);
      } else {
        return [...prev, materialId];
      }
    });
  };

  const handleSelectAll = () => {
    const allMaterialIds = marketingKitCategories?.flatMap(
      cat => cat?.materials?.map(m => m?.id)
    );
    setSelectedMaterials(allMaterialIds);
  };

  const handleDeselectAll = () => {
    setSelectedMaterials([]);
  };

  const handleDownload = () => {
    console.log('Downloading materials:', {
      materials: selectedMaterials,
      format: downloadFormat,
      customization: includeCustomization
    });
    // Simulate download
    setTimeout(() => {
      onClose?.();
    }, 1000);
  };

  const getTotalSize = () => {
    let totalBytes = 0;
    marketingKitCategories?.forEach(category => {
      category?.materials?.forEach(material => {
        if (selectedMaterials?.includes(material?.id)) {
          const sizeStr = material?.size?.replace(' MB', '')?.replace(' GB', '');
          const sizeNum = parseFloat(sizeStr);
          const multiplier = material?.size?.includes('GB') ? 1024 : 1;
          totalBytes += sizeNum * multiplier;
        }
      });
    });
    
    if (totalBytes >= 1024) {
      return `${(totalBytes / 1024)?.toFixed(2)} GB`;
    }
    return `${totalBytes?.toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Download Marketing Kit</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Select materials to include in your download package
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Selection Controls */}
        <div className="p-6 border-b border-border bg-muted/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                iconName="CheckSquare"
                iconPosition="left"
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeselectAll}
                iconName="Square"
                iconPosition="left"
              >
                Deselect All
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{selectedMaterials?.length}</span> items selected
            </div>
          </div>

          {/* Download Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Preferred Format</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDownloadFormat('pdf')}
                  className={`flex-1 px-3 py-2 text-sm rounded border transition-colors ${
                    downloadFormat === 'pdf' ?'bg-primary text-white border-primary' :'bg-background text-foreground border-border hover:border-primary'
                  }`}
                >
                  PDF
                </button>
                <button
                  onClick={() => setDownloadFormat('original')}
                  className={`flex-1 px-3 py-2 text-sm rounded border transition-colors ${
                    downloadFormat === 'original' ?'bg-primary text-white border-primary' :'bg-background text-foreground border-border hover:border-primary'
                  }`}
                >
                  Original Format
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                checked={includeCustomization}
                onCheckedChange={setIncludeCustomization}
              />
              <label className="text-sm text-foreground cursor-pointer" onClick={() => setIncludeCustomization(!includeCustomization)}>
                Include customization templates
              </label>
            </div>
          </div>
        </div>

        {/* Materials List */}
        <div className="p-6 space-y-6">
          {marketingKitCategories?.map((category, categoryIndex) => (
            <div key={categoryIndex} className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Folder" size={20} className="mr-2 text-primary" />
                {category?.category}
              </h3>
              <div className="space-y-2">
                {category?.materials?.map((material) => {
                  const isSelected = selectedMaterials?.includes(material?.id);
                  return (
                    <div
                      key={material?.id}
                      onClick={() => handleToggleMaterial(material?.id)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleMaterial(material?.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground">{material?.name}</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            {material?.description}
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center">
                              <Icon name="FileText" size={12} className="mr-1" />
                              {material?.size}
                            </span>
                            <span className="flex items-center">
                              <Icon name="Download" size={12} className="mr-1" />
                              {material?.formats?.join(', ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Info Banner */}
        <div className="px-6 pb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Customization Available</p>
                <p>Many materials can be customized with your contact information and branding. 
                Download the editable formats (PSD, HTML) to personalize them for your market.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20 sticky bottom-0">
          <div className="text-sm">
            <span className="text-muted-foreground">Total size: </span>
            <span className="font-medium text-foreground">{getTotalSize()}</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDownload}
              iconName="Download"
              iconPosition="left"
              disabled={selectedMaterials?.length === 0}
            >
              Download {selectedMaterials?.length > 0 && `(${selectedMaterials?.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingKitModal;